/** Provider-neutral role policy for low-cost extraction workers. */
export interface EconomyModelPolicy {
  readonly version: 1;
  readonly claude: {
    readonly economyAlias: string;
  };
  readonly codex: {
    readonly economySignals: readonly string[];
    readonly excludeSignals: readonly string[];
    readonly selection: "first-signal-then-catalog-priority";
    readonly fallback: string | null;
  };
}

export interface CodexModelCatalogEntry {
  readonly slug: string;
  readonly display_name?: string;
  readonly description?: string;
  readonly visibility?: string;
  readonly priority?: number;
}

export interface CodexModelCatalog {
  readonly models: readonly CodexModelCatalogEntry[];
}

const SAFE_MODEL_ID = /^[A-Za-z0-9._-]+$/;
const SAFE_SIGNAL = /^[A-Za-z0-9 -]+$/;
const MAX_SIGNALS = 64;
const MAX_SIGNAL_CHARS = 64;

export function isSafeModelId(model: string): boolean {
  return model.length > 0 && SAFE_MODEL_ID.test(model);
}

function normalizeSearchText(text: string): string {
  return ` ${text.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()} `;
}

function hasSignal(searchText: string, signal: string): boolean {
  const needle = normalizeSearchText(signal).trim();
  return needle.length > 0 && searchText.includes(` ${needle} `);
}

function validSignals(value: unknown): value is string[] {
  return Array.isArray(value) && value.length <= MAX_SIGNALS && value.every((signal) =>
    typeof signal === "string" &&
    signal.length > 0 &&
    signal.length <= MAX_SIGNAL_CHARS &&
    SAFE_SIGNAL.test(signal)
  );
}

/** Parse and validate a user-overridable policy without evaluating policy text as code or regex. */
export function parseEconomyModelPolicy(value: unknown): EconomyModelPolicy {
  if (!value || typeof value !== "object") throw new Error("model policy must be an object");
  const raw = value as Record<string, unknown>;
  const claude = raw.claude as Record<string, unknown> | undefined;
  const codex = raw.codex as Record<string, unknown> | undefined;
  const alias = typeof claude?.economyAlias === "string" ? claude.economyAlias : "";
  const fallback = codex?.fallback;
  if (raw.version !== 1 || !isSafeModelId(alias)) throw new Error("unsupported Claude economy policy");
  if (!validSignals(codex?.economySignals) || !validSignals(codex?.excludeSignals)) {
    throw new Error("invalid economy model signals");
  }
  if (codex?.selection !== "first-signal-then-catalog-priority") {
    throw new Error("unsupported economy model selection policy");
  }
  if (fallback !== null && (typeof fallback !== "string" || !isSafeModelId(fallback))) {
    throw new Error("invalid economy model fallback");
  }
  return value as EconomyModelPolicy;
}

/**
 * Select the strongest economy signal, then the provider catalog's own priority.
 * Fails closed when the catalog cannot identify an economy model.
 */
export function selectCodexEconomyModel(
  catalog: CodexModelCatalog,
  policyInput: EconomyModelPolicy | unknown,
): string {
  const policy = parseEconomyModelPolicy(policyInput);
  const models = Array.isArray(catalog?.models) ? catalog.models : [];
  const eligible = models.filter((model) => {
    if (!model || !isSafeModelId(String(model.slug || "")) || model.visibility === "hide") return false;
    const text = normalizeSearchText(`${model.slug} ${model.description || ""}`);
    return !policy.codex.excludeSignals.some((signal) => hasSignal(text, signal));
  });
  if (eligible.length === 0) throw new Error("Codex model catalog has no selectable models");

  for (const signal of policy.codex.economySignals) {
    const hits = eligible
      .filter((model) => hasSignal(normalizeSearchText(`${model.slug} ${model.description || ""}`), signal))
      .sort((a, b) => {
        const ap = Number.isFinite(a.priority) ? Number(a.priority) : Number.MAX_SAFE_INTEGER;
        const bp = Number.isFinite(b.priority) ? Number(b.priority) : Number.MAX_SAFE_INTEGER;
        return ap - bp || a.slug.localeCompare(b.slug);
      });
    if (hits.length > 0) return hits[0].slug;
  }

  if (policy.codex.fallback && eligible.some((model) => model.slug === policy.codex.fallback)) {
    return policy.codex.fallback;
  }
  throw new Error("no economy Codex model matched the current catalog");
}

export function resolveEconomyModel(
  backend: "claude" | "codex",
  policyInput: EconomyModelPolicy | unknown,
  catalog?: CodexModelCatalog,
): string {
  const policy = parseEconomyModelPolicy(policyInput);
  if (backend === "claude") return policy.claude.economyAlias;
  if (!catalog) throw new Error("Codex model catalog is required");
  return selectCodexEconomyModel(catalog, policy);
}
